import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, Wrench, Plus, ArrowRight, Settings2, Cpu } from 'lucide-react';
import { toast } from 'sonner';

const LaserEquipment = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMachines = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/laser/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMachines(res.data || []);
      } catch {
        toast.error('Failed to load laser machines');
      } finally {
        setLoading(false);
      }
    };
    fetchMachines();
  }, [token]);

  return (
    <div
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.LASER_DASHBOARD)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-2">My Equipment</div>
          <h1
            className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            My Laser Machines
          </h1>
          <p className="mt-2 text-sm text-zinc-300">
            Saved laser profiles used by the calculator and troubleshooting tools.
          </p>
        </div>

        {/* Add machine CTA */}
        <Link to="/laser/setup">
          <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add / Configure Machine
          </Button>
        </Link>

        {/* Machine list */}
        {loading ? (
          <div className="text-zinc-400 py-8 text-center">Loading...</div>
        ) : machines.length === 0 ? (
          <Card className="bg-black/60 border-zinc-700">
            <CardContent className="py-10 text-center space-y-4">
              <Cpu className="w-12 h-12 text-zinc-600 mx-auto" />
              <h3 className="text-white font-bold text-lg">No Laser Machines Yet</h3>
              <p className="text-zinc-400 text-sm">Add your laser machine to enable the calculator and troubleshooting tools.</p>
              <Link to="/laser/setup">
                <Button className="bg-[#c85a00] hover:bg-[#a84800] text-white">
                  Set Up Your First Machine
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {machines.map((machine, idx) => (
              <Card key={machine.id || idx} className="bg-black/60 border-zinc-700 hover:border-orange-500/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white flex items-center gap-2 text-lg">
                      <Zap className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      {machine.name || machine.model || 'Laser Machine'}
                    </CardTitle>
                    <Link to="/laser/setup">
                      <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300 hover:border-orange-500 hover:text-orange-400">
                        <Settings2 className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                    </Link>
                  </div>
                  {machine.brand && machine.model && (
                    <p className="text-sm text-zinc-400">{machine.brand} · {machine.model}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {machine.laser_type && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        {machine.laser_type}
                      </Badge>
                    )}
                    {machine.power_watts && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        {machine.power_watts}W
                      </Badge>
                    )}
                    {machine.bed_size && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        {machine.bed_size}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LaserEquipment;
